import { c as _c } from "react/compiler-runtime";
import { Kbd } from "@/components/ui/Kbd";
import { menuContentVariants, menuItemIndicatorClassName, menuItemVariants, menuLabelClassName, menuSeparatorClassName, menuShortcutClassName } from "@/components/ui/menu-surface";
import * as ContextMenuPrimitive from "@radix-ui/react-context-menu";
import { Check, ChevronRight } from "lucide-react";
import * as React from "react";
import cn from "twsx";

/** Use when a right-click on an item should open its actions. */
const ContextMenu = ContextMenuPrimitive.Root;
const ContextMenuTrigger = ContextMenuPrimitive.Trigger;
const ContextMenuGroup = ContextMenuPrimitive.Group;
const ContextMenuPortal = ContextMenuPrimitive.Portal;
const ContextMenuSub = ContextMenuPrimitive.Sub;
const ContextMenuRadioGroup = ContextMenuPrimitive.RadioGroup;
const ContextMenuSubTrigger = t0 => {
  const $ = _c(17);
  let children;
  let className;
  let destructive;
  let inset;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      inset,
      children,
      destructive,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = destructive;
    $[4] = inset;
    $[5] = props;
    $[6] = ref;
  } else {
    children = $[1];
    className = $[2];
    destructive = $[3];
    inset = $[4];
    props = $[5];
    ref = $[6];
  }
  let t1;
  if ($[7] !== className || $[8] !== destructive || $[9] !== inset) {
    t1 = cn(menuItemVariants({
      inset,
      destructive,
      subTrigger: true
    }), className);
    $[7] = className;
    $[8] = destructive;
    $[9] = inset;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  let t2;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ChevronRight className="ml-auto !size-3 !text-text-tertiary" />;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== children || $[13] !== props || $[14] !== ref || $[15] !== t1) {
    t3 = <ContextMenuPrimitive.SubTrigger ref={ref} className={t1} {...props}>{children}{t2}</ContextMenuPrimitive.SubTrigger>;
    $[12] = children;
    $[13] = props;
    $[14] = ref;
    $[15] = t1;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  return t3;
};
ContextMenuSubTrigger.displayName = ContextMenuPrimitive.SubTrigger.displayName;
const ContextMenuSubContent = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn(menuContentVariants(), className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <ContextMenuPrimitive.SubContent ref={ref} className={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
ContextMenuSubContent.displayName = ContextMenuPrimitive.SubContent.displayName;
const ContextMenuContent = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn(menuContentVariants(), "max-h-context-menu-available", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <ContextMenuPrimitive.Portal><ContextMenuPrimitive.Content ref={ref} className={t1} {...props} /></ContextMenuPrimitive.Portal>;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
ContextMenuContent.displayName = ContextMenuPrimitive.Content.displayName;
const ContextMenuItem = t0 => {
  const $ = _c(14);
  let className;
  let destructive;
  let inset;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      inset,
      destructive,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = destructive;
    $[3] = inset;
    $[4] = props;
    $[5] = ref;
  } else {
    className = $[1];
    destructive = $[2];
    inset = $[3];
    props = $[4];
    ref = $[5];
  }
  let t1;
  if ($[6] !== className || $[7] !== destructive || $[8] !== inset) {
    t1 = cn(menuItemVariants({
      inset,
      destructive
    }), className);
    $[6] = className;
    $[7] = destructive;
    $[8] = inset;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== props || $[11] !== ref || $[12] !== t1) {
    t2 = <ContextMenuPrimitive.Item ref={ref} className={t1} {...props} />;
    $[10] = props;
    $[11] = ref;
    $[12] = t1;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  return t2;
};
ContextMenuItem.displayName = ContextMenuPrimitive.Item.displayName;
const ContextMenuCheckboxItem = t0 => {
  const $ = _c(15);
  let checked;
  let children;
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      children,
      checked,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = checked;
    $[2] = children;
    $[3] = className;
    $[4] = props;
    $[5] = ref;
  } else {
    checked = $[1];
    children = $[2];
    className = $[3];
    props = $[4];
    ref = $[5];
  }
  let t1;
  if ($[6] !== className) {
    t1 = cn(menuItemVariants({
      indicator: true
    }), className);
    $[6] = className;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className={menuItemIndicatorClassName}><ContextMenuPrimitive.ItemIndicator><Check /></ContextMenuPrimitive.ItemIndicator></span>;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== checked || $[10] !== children || $[11] !== props || $[12] !== ref || $[13] !== t1) {
    t3 = <ContextMenuPrimitive.CheckboxItem ref={ref} className={t1} checked={checked} {...props}>{t2}{children}</ContextMenuPrimitive.CheckboxItem>;
    $[9] = checked;
    $[10] = children;
    $[11] = props;
    $[12] = ref;
    $[13] = t1;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
};
ContextMenuCheckboxItem.displayName = ContextMenuPrimitive.CheckboxItem.displayName;
const ContextMenuRadioItem = t0 => {
  const $ = _c(13);
  let children;
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = ref;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    ref = $[4];
  }
  let t1;
  if ($[5] !== className) {
    t1 = cn(menuItemVariants({
      indicator: true
    }), className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className={menuItemIndicatorClassName}><ContextMenuPrimitive.ItemIndicator><Check /></ContextMenuPrimitive.ItemIndicator></span>;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== children || $[9] !== props || $[10] !== ref || $[11] !== t1) {
    t3 = <ContextMenuPrimitive.RadioItem ref={ref} className={t1} {...props}>{t2}{children}</ContextMenuPrimitive.RadioItem>;
    $[8] = children;
    $[9] = props;
    $[10] = ref;
    $[11] = t1;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return t3;
};
ContextMenuRadioItem.displayName = ContextMenuPrimitive.RadioItem.displayName;
const ContextMenuLabel = t0 => {
  const $ = _c(12);
  let className;
  let inset;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      inset,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = inset;
    $[3] = props;
    $[4] = ref;
  } else {
    className = $[1];
    inset = $[2];
    props = $[3];
    ref = $[4];
  }
  const t1 = inset && "pl-8";
  let t2;
  if ($[5] !== className || $[6] !== t1) {
    t2 = cn(menuLabelClassName, t1, className);
    $[5] = className;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== props || $[9] !== ref || $[10] !== t2) {
    t3 = <ContextMenuPrimitive.Label ref={ref} className={t2} {...props} />;
    $[8] = props;
    $[9] = ref;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
};
ContextMenuLabel.displayName = ContextMenuPrimitive.Label.displayName;
const ContextMenuSeparator = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn(menuSeparatorClassName, className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <ContextMenuPrimitive.Separator ref={ref} className={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
ContextMenuSeparator.displayName = ContextMenuPrimitive.Separator.displayName;
const ContextMenuShortcut = t0 => {
  const $ = _c(5);
  const {
    keys,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn(menuShortcutClassName, className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== keys || $[3] !== t1) {
    t2 = <Kbd keys={keys} variant="inline" className={t1} />;
    $[2] = keys;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
ContextMenuShortcut.displayName = "ContextMenuShortcut";
export { ContextMenu, ContextMenuCheckboxItem, ContextMenuContent, ContextMenuGroup, ContextMenuItem, ContextMenuLabel, ContextMenuPortal, ContextMenuRadioGroup, ContextMenuRadioItem, ContextMenuSeparator, ContextMenuShortcut, ContextMenuSub, ContextMenuSubContent, ContextMenuSubTrigger, ContextMenuTrigger };